/**
 * زمینهٔ یک نفر داخلِ «ویرایش رکورد حقوق» — تا بدونِ بستنِ پنجره معلوم باشد
 * این اعداد از کجا آمده‌اند.
 *
 * چرا: کاربر می‌خواست موقعِ ویرایش، همان‌جا ببیند «چند روز غیبت خورده، چند دقیقه
 * کسری، کارمزدش بابتِ چه عملیاتی بوده، چه پرداختی/دریافتی‌ای این ماه داشته» —
 * نه اینکه برای هر کدام برود صفحهٔ دیگری.
 *
 * همه‌چیزِ این پنل **فقط‌خواندنی** است: ویرایشِ واقعیِ کارکرد باید در «تولید»
 * انجام شود. اصلاحِ حقوقیِ همین ماه با «ردیف‌های اصلاحی» (جدولِ پایین‌تر) است؛
 * دکمهٔ «اصلاح» کنارِ هر عملیات همان ردیف را با نام و فی پیش‌پر می‌کند.
 */
import React from "react";
import { Col, Collapse, Descriptions, Empty, Row, Statistic, Table, Tag, Typography } from "antd";
import Alert from "./GxAlert";
import { formatMoney } from "../utils/money";
import { fmtNum, toJalali } from "../utils/dateUtils";
import { PlanPreview } from "../pages/employees/PayRolesEditor";

const { Text } = Typography;
const money = (v) => formatMoney(v);

export default function RecordContextPanel({ explain, dossier, loading, onCorrect }) {
  if (loading) return <Text type="secondary">در حال بارگذاری جزئیات…</Text>;
  if (!explain && !dossier) return null;

  const att = explain?.attendance;
  const emp = explain?.employee;
  const ins = explain?.insurance;
  const piece = dossier?.piece_breakdown || [];
  const prodLines = dossier?.production_lines || [];
  const ledger = dossier?.ledger_transactions || [];
  const cardex = dossier?.cardex_summary;
  const rec = dossier?.record;

  const ledgerDebit = ledger.filter((t) => t.counted && t.direction === "debit")
    .reduce((a, t) => a + Number(t.amount || 0), 0);
  const ledgerCredit = ledger.filter((t) => t.counted && t.direction === "credit")
    .reduce((a, t) => a + Number(t.amount || 0), 0);

  return (
    <div style={{ marginBottom: 14 }}>
      {(explain?.warnings || []).map((w, i) => (
        <Alert key={i} type="warning" showIcon style={{ marginBottom: 8 }} message={w} />
      ))}
      {rec?.manual_override && (
        <Alert type="info" showIcon style={{ marginBottom: 8 }}
          message="این رکورد دستی ویرایش شده؛ «محاسبهٔ مجدد» رویش نمی‌نویسد و ردیف‌های اصلاحی فقط تفاضلشان را اعمال می‌کنند." />
      )}

      {/* ── حضور و غیاب ── (غیرحضوری: فقط اطلاعاتی و خاکستری — کسری نمی‌خورد، پس قرمز گمراه‌کننده است) */}
      {att && emp && emp.attendance_required === false && (
        <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
          آمارِ دستگاه فقط برای اطلاع است — این نفر حضوری نیست و غیبت/کسری از حقوقش کم نمی‌شود.
        </Text>
      )}
      {att && (
        <Row gutter={8} style={{ marginBottom: 10, opacity: emp?.attendance_required === false ? 0.45 : 1 }}>
          <Col xs={6}><Statistic title="روزِ کاری" value={att.working_days}
            valueStyle={{ fontSize: 18 }} /></Col>
          <Col xs={6}><Statistic title="حاضر" value={att.present_days}
            valueStyle={{ fontSize: 18, color: att.present_days ? "#107C41" : "#C0392B" }} /></Col>
          <Col xs={6}><Statistic title="غایب" value={att.missing_days}
            valueStyle={{ fontSize: 18, color: att.missing_days ? "#C0392B" : undefined }} /></Col>
          <Col xs={6}><Statistic title="کسریِ کار (دقیقه)" value={fmtNum(att.shortage_minutes)}
            valueStyle={{ fontSize: 18 }} /></Col>
        </Row>
      )}

      {/* ── آنچه از پایه کم شده ── */}
      {emp?.monthly_salary > 0 && explain?.record && emp?.attendance_required !== false
        && !(dossier?.pay_plan?.ranges?.length) && (
        <Descriptions size="small" bordered column={2} style={{ marginBottom: 10 }}>
          <Descriptions.Item label="حقوق ثابت">{money(emp.monthly_salary)}</Descriptions.Item>
          <Descriptions.Item label="کسرِ غیبت و کسریِ کار">
            <span style={{ color: "#C0392B" }}>
              {money(Math.max(emp.monthly_salary - explain.record.gross_pay, 0))}
            </span>
          </Descriptions.Item>
        </Descriptions>
      )}

      {/* ── کارکردِ کارمزدی ── */}
      {piece.length > 0 && (
        <>
          <Text strong style={{ fontSize: 12 }}>کارکردِ کارمزدی (به تفکیکِ عملیات و سایز)</Text>
          <Table size="small" pagination={false} rowKey={(r, i) => `${r.operation_name}-${i}`}
            style={{ marginTop: 4, marginBottom: 6 }}
            dataSource={piece}
            columns={[
              { title: "عملیات", dataIndex: "operation_name" },
              // سریِ دوخت = کدِ تولید؛ بدونش فیش با برگه‌های کار تطبیق‌پذیر نیست
              { title: "سری دوخت", dataIndex: "production_code", align: "center",
                render: (v) => v || "—" },
              // v2.156.0 (کاربر): «هر سایز جدا، نه تجمیعیِ پارت»
              { title: "سایز", dataIndex: "size_label", align: "center", render: (v) => v || "—" },
              // برش: «جین + تکی» و فیِ هر جین (نه فیِ هر عدد) — v2.115.0
              { title: "تعداد", dataIndex: "quantity", align: "center",
                render: (v, r) => (r.qty_label ? <span title={`${fmtNum(v)} عدد`}>{r.qty_label}</span> : fmtNum(v)) },
              { title: "فی", dataIndex: "unit_price", align: "left",
                render: (v, r) => r.rate_label || money(v) },
              { title: "مبلغ (ریال)", dataIndex: "amount", align: "left",
                render: (v) => money(v) },
              ...(onCorrect ? [{
                title: "", key: "_fix", width: 60, align: "center",
                render: (_, r) => (
                  // ردیفِ اصلاحی با همین عملیات و فی پیش‌پر می‌شود (تعداد را کاربر می‌زند)
                  <a onClick={() => onCorrect(r)} title="افزودن ردیفِ اصلاحی برای این عملیات">اصلاح</a>
                ),
              }] : []),
            ]}
            summary={(rows) => {
              const tq = rows.reduce((a, r) => a + Number(r.quantity || 0), 0);
              const ta = rows.reduce((a, r) => a + Number(r.amount || 0), 0);
              return (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}><b>جمع</b></Table.Summary.Cell>
                  <Table.Summary.Cell index={1} />
                  <Table.Summary.Cell index={2} align="center"><b>{fmtNum(tq)}</b></Table.Summary.Cell>
                  {/* میانگینِ فی — تا معلوم شود کار به چه نرخی حساب شده */}
                  <Table.Summary.Cell index={3} align="left">
                    <b>میانگین {money(tq ? Math.round(ta / tq) : 0)}</b>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="left"><b>{money(ta)}</b></Table.Summary.Cell>
                  {onCorrect && <Table.Summary.Cell index={5} />}
                </Table.Summary.Row>
              );
            }} />
        </>
      )}

      {/* نقش‌ها و بازه‌های ماه (تکی/ترکیبی) — همان چیزی که موتور حساب کرده (v2.108.0) */}
      {dossier?.pay_plan?.ranges?.length > 0 && (
        <div style={{ marginBottom: 8 }}><PlanPreview plan={dossier.pay_plan} compact /></div>
      )}

      {/* ── ریزِ ردیف‌های تولید (روز‌به‌روز) — بسته، تا پنل شلوغ نشود ── */}
      {prodLines.length > 0 && (
        <Collapse size="small" ghost style={{ marginBottom: 10 }} items={[{
          key: "prod",
          label: <Text type="secondary" style={{ fontSize: 12 }}>
            ریزِ ردیف‌های تولید — {fmtNum(prodLines.length)} ردیف، جمعِ مؤثر {money(dossier.production_total)}
          </Text>,
          children: (
            <Table size="small" pagination={prodLines.length > 40 ? { pageSize: 40, size: "small" } : false}
              rowKey="id" dataSource={prodLines}
              rowClassName={(p) => (p.counted === false ? "gx-row-muted" : "")}
              columns={[
                { title: "تاریخ", dataIndex: "date", width: 100, render: (v) => toJalali(v) },
                { title: "عملیات", dataIndex: "operation_name", render: (v, p) => (
                  <>{v || "—"}{p.counted === false && <Tag style={{ marginRight: 6 }}>خارج از بازهٔ کارمزدی</Tag>}</>
                ) },
                { title: "تعداد", dataIndex: "quantity", align: "center", width: 80, render: (v) => fmtNum(v) },
                { title: "فی", dataIndex: "unit_price", align: "left", width: 110, render: (v) => money(v) },
                { title: "مبلغ", dataIndex: "line_total", align: "left", width: 120, render: (v) => money(v) },
              ]} />
          ),
        }]} />
      )}

      {/* ── پرداختی‌ها و دریافتی‌های این ماه (دفتر مالی) ── */}
      {ledger.length > 0 && (
        <>
          <Text strong style={{ fontSize: 12 }}>پرداختی‌ها و دریافتی‌های این ماه (دفتر مالی)</Text>
          <Table size="small" pagination={false} rowKey="id"
            style={{ marginTop: 4, marginBottom: 10 }}
            dataSource={ledger}
            rowClassName={(t) => (t.counted ? "" : "gx-row-muted")}
            columns={[
              { title: "تاریخ", dataIndex: "occurred_at", width: 100, render: (v) => toJalali(v) },
              { title: "نوع", dataIndex: "label" },
              { title: "شرح", dataIndex: "description", render: (v) => v || "—" },
              { title: "مبلغ (ریال)", dataIndex: "amount", align: "left",
                render: (v, t) => (
                  <span style={{ color: t.direction === "credit" ? "#107C41" : "#C0392B", opacity: t.counted ? 1 : 0.6 }}>
                    {t.direction === "credit" ? "+" : "−"}{money(v)}
                  </span>
                ) },
              { title: "در محاسبه", dataIndex: "counted", align: "center", width: 130,
                render: (v, t) => (v
                  ? <Tag color={t.direction === "credit" ? "green" : "volcano"}>{t.direction === "credit" ? "اضافه می‌شود" : "کسر می‌شود"}</Tag>
                  : <Tag>{t.why_not || "شمرده نمی‌شود"}</Tag>) },
            ]}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={3}><b>جمعِ مؤثر در حقوق</b></Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="left">
                  <b style={{ color: "#C0392B" }}>−{money(ledgerDebit)}</b>
                  {ledgerCredit > 0 && <> / <b style={{ color: "#107C41" }}>+{money(ledgerCredit)}</b></>}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} />
              </Table.Summary.Row>
            )} />
        </>
      )}

      {/* ── بیمه و کاردکس ── */}
      <Row gutter={8}>
        {ins && (
          <Col xs={24} md={12}>
            <Descriptions size="small" bordered column={1}>
              <Descriptions.Item label="حق بیمه">{money(ins.premium)}</Descriptions.Item>
              <Descriptions.Item label="خالص پرداختی (لیست بیمه)">{money(ins.net_payable)}</Descriptions.Item>
            </Descriptions>
          </Col>
        )}
        {cardex && (
          <Col xs={24} md={12}>
            <Descriptions size="small" bordered column={1}>
              <Descriptions.Item label="غیبت (کاردکس)">{cardex.absent ?? "—"}</Descriptions.Item>
              <Descriptions.Item label="ناقص">{cardex.incomplete ?? "—"}</Descriptions.Item>
            </Descriptions>
          </Col>
        )}
      </Row>

      {piece.length === 0 && ledger.length === 0 && !att && <Empty description="جزئیاتی برای این نفر نیست" />}
    </div>
  );
}
